import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Header() {
  const { user, signOut, userRole } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const truncateEmail = (email) => {
    if (!email) return '';
    if (email.length <= 22) return email;
    const [local, domain] = email.split('@');
    if (!domain) return email.slice(0, 20) + '…';
    return local.slice(0, 10) + '…@' + domain;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/questionnaire', label: 'Quiz' },
    { to: '/candidates', label: 'Candidates' },
    { to: '/how-it-works', label: 'How It Works' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-[#0F2A4A] text-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="shrink-0">
            <span className="text-2xl font-extrabold tracking-tight">
              BE<span className="text-[#D4AF37]">Civitas</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive(to)
                    ? 'bg-[#D4AF37] text-[#0F2A4A]'
                    : 'text-gray-300 hover:bg-[#1a3a5e] hover:text-white'
                }`}
              >
                {label}
              </Link>
            ))}
            {userRole === 'admin' && (
              <Link
                to="/admin"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive('/admin')
                    ? 'bg-[#D4AF37] text-[#0F2A4A]'
                    : 'text-[#D4AF37] hover:bg-[#1a3a5e]'
                }`}
              >
                Admin
              </Link>
            )}
          </nav>

          {/* User menu */}
          <div className="flex items-center gap-3">
            {!user && <Link to="/login" className="text-sm text-white">Sign in (optional)</Link>}
            {user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 bg-[#1a3a5e] hover:bg-[#1e4470] text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 max-w-[180px]"
                >
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0F2A4A] font-bold text-xs shrink-0">
                    {user.email?.charAt(0).toUpperCase()}
                  </div>
                  <span className="truncate">{truncateEmail(user.email)}</span>
                  <svg
                    className={`w-3.5 h-3.5 shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-xs text-gray-500 font-medium">Signed in as</p>
                      <p className="text-sm text-[#0F2A4A] font-semibold truncate">{user.email}</p>
                      {userRole === 'admin' && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-[#D4AF37] text-[#0F2A4A] text-xs font-bold rounded-sm">Admin</span>
                      )}
                    </div>
                    <Link
                      to="/feedback"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Feedback
                    </Link>
                    <button
                      onClick={() => { setDropdownOpen(false); signOut(); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-[#1a3a5e] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-3 pt-1 border-t border-[#1a3a5e]">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 rounded-md text-sm font-medium mb-1 ${
                  isActive(to)
                    ? 'bg-[#D4AF37] text-[#0F2A4A]'
                    : 'text-gray-300 hover:bg-[#1a3a5e] hover:text-white'
                }`}
              >
                {label}
              </Link>
            ))}
            {userRole === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-sm font-medium text-[#D4AF37] hover:bg-[#1a3a5e] mb-1"
              >
                Admin
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
